import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { RefreshCw, TrendingUp, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { kpiAPI } from '@/lib/api';
import { UmsatzBlock } from '@/components/UmsatzSperre';

/* Cashflow & Prognose (22.09.2026, Omoplata-Parität 2.4/5.1; auf Richtigkeit umgebaut 25.10.2026) — Seitentyp E:
   KPI-Kacheln, Trend-Sätze, Verlauf 12 Monate, Liste „gefährdet". Rein lesend. Soll und Zahlungsstand kommen aus dem
   Rechnungslauf und dem Stand jeder Rechnung (backend/kennzahlen/cashflow.py), nicht mehr aus den Mitgliedschaften. */

const euro = (v) => (v == null ? '–' : `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`);
const fmt = (k) => (k.wert == null ? '–' : k.einheit === '€' ? euro(k.wert) : k.einheit === '%' ? `${k.wert} %` : String(k.wert));

function Kachel({ k, onOeffnen }) {
  return (
    <Card className={`border-border/60 ${k.ziel ? 'cursor-pointer hover:border-primary/40' : ''}`} data-testid={`cf-${k.key}`}
          onClick={k.ziel ? () => onOeffnen(k.ziel) : undefined}>
      <CardContent className="p-3 sm:p-4 space-y-1">
        <p className="text-[10px] sm:text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{k.label}</p>
        <p className="text-xl sm:text-2xl font-bold tracking-tight tabular-nums">{fmt(k)}</p>
        {k.zusatz && <p className="text-[10px] text-muted-foreground">{k.zusatz}</p>}
      </CardContent>
    </Card>
  );
}

const STUFE = {
  warnung: { Icon: AlertTriangle, cls: 'text-red-600 bg-red-500/5 border-red-500/20' },
  hinweis: { Icon: Info, cls: 'text-amber-700 bg-amber-500/5 border-amber-500/20' },
  gut: { Icon: CheckCircle2, cls: 'text-emerald-700 bg-emerald-500/5 border-emerald-500/20' },
};

export default function CashflowPage() {
  const navigate = useNavigate();
  const [daten, setDaten] = useState(null);
  const [laden, setLaden] = useState(true);
  const [gesperrt, setGesperrt] = useState(false);   // Umsatz-Sperre (30.10.2026): der Server antwortet 403, bis der Inhaber entsperrt hat

  const lade = useCallback(async () => {
    setLaden(true);
    try { setDaten((await kpiAPI.cashflow()).data); }
    catch (e) {
      if (e?.response?.status === 403) setGesperrt(true);
      else toast.error(e?.response?.data?.detail || 'Cashflow konnte nicht geladen werden');
    }
    finally { setLaden(false); }
  }, []);
  useEffect(() => { lade(); }, [lade]);

  const verlauf = (daten?.verlauf || []).map((p) => ({ ...p, monat: p.monat.slice(2) }));

  return (
    <div className="space-y-5 fade-in" data-testid="cashflow-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><TrendingUp className="w-6 h-6 text-primary" /> Cashflow & Prognose</h1>
          <p className="text-muted-foreground text-sm">Soll und Zahlungsstand aus dem Rechnungslauf, gefährdete Mitglieder aus der Teilnahme, Auslastung aus dem Kursplan</p>
        </div>
        <Button variant="outline" size="sm" onClick={lade} disabled={laden} className="gap-1.5"><RefreshCw className={`w-3.5 h-3.5 ${laden ? 'animate-spin' : ''}`} /> Aktualisieren</Button>
      </div>

      {gesperrt ? (
        <UmsatzBlock gesperrt titel="Cashflow und Prognose" />
      ) : laden && !daten ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[...Array(7)].map((_, i) => <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />)}</div>
      ) : daten && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {daten.kacheln.map((k) => <Kachel key={k.key} k={k} onOeffnen={navigate} />)}
          </div>
          {daten.hinweis && <p className="text-xs text-muted-foreground" data-testid="cf-hinweis">{daten.hinweis}</p>}

          {daten.trends?.length > 0 && (
            <div className="space-y-2" data-testid="cf-trends">
              {daten.trends.map((t, i) => { const S = STUFE[t.stufe] || STUFE.hinweis; return (
                <div key={i} className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${S.cls}`}><S.Icon className="w-4 h-4 flex-shrink-0" />{t.text}</div>
              ); })}
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-4">
            <Card className="border-border/60 lg:col-span-2">
              <CardHeader className="pb-2"><CardTitle className="text-base">Bestand und Beiträge — 12 Monate</CardTitle></CardHeader>
              <CardContent>
                {verlauf.length < 2 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">Der Verlauf füllt sich mit den nächtlichen Snapshots — ab nächstem Monat sichtbar.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={verlauf} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="monat" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                      <Tooltip formatter={(v, n) => (n === 'Beiträge' ? euro(v) : v)} />
                      <Legend />
                      <Line yAxisId="l" type="monotone" dataKey="mitglieder" name="Mitglieder" stroke="#E60000" strokeWidth={2} dot={false} />
                      <Line yAxisId="r" type="monotone" dataKey="mrr" name="Beiträge" stroke="#1e3a5f" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card className="border-border/60">
              <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2"><CardTitle className="text-base">Handlungsbedarf: {daten.gefaehrdet.tage} Tage ohne Training</CardTitle>
                <button type="button" className="text-xs text-primary hover:underline" onClick={() => navigate('/reaktivierung')}>Rückrufliste</button></CardHeader>
              <CardContent className="p-0">
                {daten.gefaehrdet.liste.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-4">Alle aktiven Mitglieder waren in den letzten {daten.gefaehrdet.tage} Tagen im Training.</p>
                ) : (
                  <ul className="divide-y divide-border/40 max-h-[300px] overflow-y-auto">
                    {daten.gefaehrdet.liste.map((m) => (
                      <li key={m.member_id} className="px-4 py-2 text-sm flex items-center justify-between gap-2">
                        <button type="button" className="hover:underline text-left truncate" onClick={() => navigate(`/leads/${m.member_id}`)}>{m.name || m.member_id}</button>
                        <span className="text-xs text-muted-foreground flex-shrink-0">{m.tage_seit == null ? 'noch nie' : `${m.tage_seit} Tage`}</span>
                      </li>
                    ))}
                    {daten.gefaehrdet.anzahl > daten.gefaehrdet.liste.length && (
                      <li className="px-4 py-2 text-xs text-muted-foreground">… und {daten.gefaehrdet.anzahl - daten.gefaehrdet.liste.length} weitere — vollständig unter Teilnahme</li>
                    )}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
